import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPipeGenerator } from '../utils/flappyGenerator';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function drawRoundedRect(ctx, x, y, w, h, r) {
  const radius = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function computeDifficultyParams({ score, timeMs }, config) {
  // Very easy for first 30s, then bump difficulty every 20s.
  const s = Math.max(0, Number(score || 0));
  const t = Math.max(0, Number(timeMs || 0)) / 1000;
  const level = t <= 30 ? 0 : Math.floor((t - 30) / 20) + 1;
  const difficulty = clamp(1 + level * 0.12 + s * 0.008, 1, 2.6);
  const scrollSpeed = clamp(config.baseScrollSpeed * difficulty, config.baseScrollSpeed, config.maxScrollSpeed);
  return { scrollSpeed, level };
}

function gapSizeFromProgress({ score, timeMs }, config) {
  const s = Math.max(0, Number(score || 0));
  const t = Math.max(0, Number(timeMs || 0)) / 1000;
  const level = t <= 30 ? 0 : Math.floor((t - 30) / 20) + 1;
  const reduction = Math.min(level * 10 + s * 0.4, 55);
  return Math.max(config.baseGapSize - reduction, config.minGapSize);
}

function getCoinVisualByValue(value) {
  const v = Number(value || 0);
  if (v >= 5) return { type: 'gold', color: '#FFD700' };
  if (v >= 3) return { type: 'silver', color: '#C0C0C0' };
  return { type: 'bronze', color: '#CD7F32' };
}

function defaultSize() {
  return { width: 820, height: 460 };
}

export default function FlappyCanvas({
  seed,
  durationSeconds = 60,
  mode = 'play',
  ghostRun = null,
  onFinish,
  onFlap,
  disabled = false,
  allowPractice = false,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const pipeGeneratorRef = useRef(null);
  const assetsRef = useRef({});
  const audioCtxRef = useRef(null);

  const [status, setStatus] = useState('ready'); // ready | running | ended
  const [score, setScore] = useState(0);
  const [coinsCollected, setCoinsCollected] = useState(0);
  const [endedReason, setEndedReason] = useState(null);
  const [endedAtMs, setEndedAtMs] = useState(0);
  const [practiceActive, setPracticeActive] = useState(false);

  const flapTimesRef = useRef([]);
  const inputEnabledRef = useRef(false);
  const lastPointerFlapAtRef = useRef(0);

  const config = useMemo(() => {
    const { width, height } = defaultSize();

    const dur = Number(durationSeconds);
    const isTimed = Number.isFinite(dur) && dur > 0;

    return {
      width,
      height,
      birdX: 170,
      birdRadius: 12,
      gravity: 1050,
      flapVel: -330,
      maxMs: isTimed ? Math.max(10, dur) * 1000 : Infinity,

      pipeWidth: 70,
      gapMargin: 90,

      baseGapSize: 180,
      minGapSize: 110,

      basePipeSpacing: 270,
      minPipeSpacing: 190,

      baseScrollSpeed: 170, // px/s
      maxScrollSpeed: 300,

      // Legacy tunables kept for compatibility; actual speed ramp uses example.html score formula.
      difficultySpeedPerPoint: 0,
      difficultySpeedPerSecond: 0,
      // Intentionally disabled for consistency (Chrome-offline feel).
      difficultySpacingTightenPerPoint: 0,
      difficultySpacingTightenPerSecond: 0,
      difficultyGapTightenPerPoint: 0,
      difficultyGapTightenPerSecond: 0,

      };
      }, [durationSeconds]);

  const stateRef = useRef({
    timeMs: 0,
    birdY: 230,
    birdV: 0,
    pipes: [],
    pipeIndex: 0,
    score: 0,
    coinsCollected: 0,

    // visuals
    clouds: [],
    particles: [],
    coinEffects: [],
    birdFrame: 0,
    birdFrameTimerMs: 0,

    // ghost
    ghostTimeMs: 0,
    ghostY: 230,
    ghostV: 0,
    ghostFlapIndex: 0,
  });

  useEffect(() => {
    const generator = createPipeGenerator(seed);
    pipeGeneratorRef.current = generator;
    const initialPipes = generator.generateInitialPipes({
      score: 0,
      height: config.height,
      width: config.width,
      gapMargin: config.gapMargin,
      groundHeight: 40,
      count: 10,
      spacing: config.basePipeSpacing,
    });

    // Reset when seed changes
    setStatus('ready');
    setScore(0);
    setEndedReason(null);
    setEndedAtMs(0);
    setPracticeActive(false);
    setCoinsCollected(0);
    flapTimesRef.current = [];
    inputEnabledRef.current = false;

    stateRef.current = {
      timeMs: 0,
      birdY: 230,
      birdV: 0,
      pipes: initialPipes,
      pipeIndex: initialPipes.length,
      score: 0,
      coinsCollected: 0,

      clouds: [],
      particles: [],
      coinEffects: [],
      birdFrame: 0,
      birdFrameTimerMs: 0,
      ghostTimeMs: 0,
      ghostY: 230,
      ghostV: 0,
      ghostFlapIndex: 0,
    };
  }, [config, seed]);

  // Build procedural assets per example.html (background, bird frames, pipe texture, coin sprites, particle texture)
  useEffect(() => {
    const { width, height } = config;

    const makeCanvas = (w, h) => {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      return c;
    };

    // Background
    const bg = makeCanvas(width, height);
    const bgCtx = bg.getContext('2d');
    if (bgCtx) {
      const grad = bgCtx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, '#87CEEB');
      grad.addColorStop(0.55, '#98D8F0');
      grad.addColorStop(1, '#0b1320');
      bgCtx.fillStyle = grad;
      bgCtx.fillRect(0, 0, width, height);

      // Sun
      bgCtx.beginPath();
      bgCtx.arc(width - 90, 70, 44, 0, Math.PI * 2);
      bgCtx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      bgCtx.fill();

      // Sun glow
      bgCtx.beginPath();
      bgCtx.arc(width - 90, 70, 70, 0, Math.PI * 2);
      const sunGrad = bgCtx.createRadialGradient(width - 90, 70, 44, width - 90, 70, 70);
      sunGrad.addColorStop(0, 'rgba(255,255,255,0.25)');
      sunGrad.addColorStop(1, 'rgba(255,255,255,0)');
      bgCtx.fillStyle = sunGrad;
      bgCtx.fill();
    }

    // Bird frames (3)
    const birdFrames = [];
    for (let frame = 0; frame < 3; frame += 1) {
      const bird = makeCanvas(60, 60);
      const bctx = bird.getContext('2d');
      if (bctx) {
        // Body
        bctx.beginPath();
        bctx.ellipse(30, 30, 20, 15, 0, 0, Math.PI * 2);
        const bodyGrad = bctx.createLinearGradient(10, 10, 50, 50);
        bodyGrad.addColorStop(0, '#FFD700');
        bodyGrad.addColorStop(1, '#FFA500');
        bctx.fillStyle = bodyGrad;
        bctx.fill();

        // Wing
        const wingY = 30 + Math.sin(frame * 1.5) * 5;
        bctx.beginPath();
        bctx.ellipse(25, wingY, 15, 10, -Math.PI / 4, 0, Math.PI * 2);
        bctx.fillStyle = '#FF8C00';
        bctx.fill();

        // Eye
        bctx.beginPath();
        bctx.arc(40, 25, 6, 0, Math.PI * 2);
        bctx.fillStyle = '#000';
        bctx.fill();
        bctx.beginPath();
        bctx.arc(42, 23, 2, 0, Math.PI * 2);
        bctx.fillStyle = '#FFF';
        bctx.fill();

        // Beak
        bctx.beginPath();
        bctx.moveTo(55, 30);
        bctx.lineTo(65, 25);
        bctx.lineTo(65, 35);
        bctx.closePath();
        bctx.fillStyle = '#FF4500';
        bctx.fill();

        // Outline
        bctx.strokeStyle = 'rgba(0,0,0,0.35)';
        bctx.lineWidth = 2;
        bctx.stroke();
      }
      birdFrames.push(bird);
    }

    // Pipe texture
    const pipeTex = makeCanvas(80, 50);
    const pctx = pipeTex.getContext('2d');
    if (pctx) {
      const pg = pctx.createLinearGradient(0, 0, 80, 0);
      pg.addColorStop(0, '#2E8B57');
      pg.addColorStop(0.5, '#3CB371');
      pg.addColorStop(1, '#2E8B57');
      pctx.fillStyle = pg;
      pctx.fillRect(0, 0, 80, 50);
      pctx.fillStyle = '#228B22';
      pctx.fillRect(0, 0, 80, 10);
      pctx.fillRect(0, 40, 80, 10);
      for (let i = 0; i < 3; i += 1) {
        pctx.fillStyle = '#32CD32';
        pctx.fillRect(10 + i * 20, 15, 10, 20);
      }
    }

    // Coin sprites
    const coinTypes = {
      bronze: {
        value: 1,
        color: '#CD7F32',
        light: '#FFE0B2',
        dark: '#8B4513',
        glow: 'rgba(205,127,50,0.85)',
      },
      silver: {
        value: 3,
        color: '#C0C0C0',
        light: '#FFFFFF',
        dark: '#6F6F6F',
        glow: 'rgba(192,192,192,0.80)',
      },
      gold: {
        value: 5,
        color: '#FFD700',
        light: '#FFF4B0',
        dark: '#B8860B',
        glow: 'rgba(255,215,0,0.85)',
      },
    };
    const coinSprites = {};
    Object.entries(coinTypes).forEach(([type, cfg]) => {
      const coin = makeCanvas(30, 30);
      const cctx = coin.getContext('2d');
      if (cctx) {
        // glow
        cctx.beginPath();
        cctx.arc(15, 15, 14, 0, Math.PI * 2);
        const glow = cctx.createRadialGradient(15, 15, 8, 15, 15, 14);
        glow.addColorStop(0, cfg.glow || cfg.color);
        glow.addColorStop(1, 'rgba(0,0,0,0)');
        cctx.fillStyle = glow;
        cctx.fill();

        // body
        cctx.beginPath();
        cctx.arc(15, 15, 12, 0, Math.PI * 2);
        const cg = cctx.createRadialGradient(10, 10, 5, 15, 15, 12);
        cg.addColorStop(0, cfg.light || '#FFFFFF');
        cg.addColorStop(0.35, cfg.color);
        cg.addColorStop(0.75, cfg.color);
        cg.addColorStop(1, cfg.dark || '#333333');
        cctx.fillStyle = cg;
        cctx.fill();

        // shine
        cctx.beginPath();
        cctx.arc(10, 10, 5, 0, Math.PI * 2);
        cctx.fillStyle = 'rgba(255,255,255,0.55)';
        cctx.fill();

        // text
        cctx.fillStyle = '#000';
        cctx.font = 'bold 10px Arial';
        cctx.textAlign = 'center';
        cctx.textBaseline = 'middle';
        cctx.fillText(String(cfg.value), 15, 15);

        // outline
        cctx.beginPath();
        cctx.arc(15, 15, 12, 0, Math.PI * 2);
        cctx.strokeStyle = '#000';
        cctx.lineWidth = 1;
        cctx.stroke();
      }
      coinSprites[type] = coin;
    });

    // Particle texture
    const particle = makeCanvas(20, 20);
    const prctx = particle.getContext('2d');
    if (prctx) {
      prctx.beginPath();
      prctx.arc(10, 10, 10, 0, Math.PI * 2);
      const g = prctx.createRadialGradient(5, 5, 0, 10, 10, 10);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      prctx.fillStyle = g;
      prctx.fill();
    }

    assetsRef.current = {
      background: bg,
      birdFrames,
      pipeTexture: pipeTex,
      coinSprites,
      particle,
    };

    // Clouds (kept in stateRef so the RAF loop can animate)
    const clouds = [];
    for (let i = 0; i < 5; i += 1) {
      clouds.push({
        x: Math.random() * width,
        y: 30 + Math.random() * 120,
        speed: 10 + Math.random() * 18, // px/s
        size: 26 + Math.random() * 40,
      });
    }
    stateRef.current.clouds = clouds;
  }, [config]);

  const playCoinSound = useCallback((value) => {
    try {
      if (typeof window === 'undefined') return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        // Best-effort resume (allowed after user gesture)
        ctx.resume();
      }

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      if (value === 1) osc.frequency.value = 523.25;
      else if (value === 3) osc.frequency.value = 659.25;
      else osc.frequency.value = 783.99;
      gain.gain.value = 0.08;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.stop(ctx.currentTime + 0.18);
    } catch {
      // ignore audio failures
    }
  }, []);

  useEffect(() => {
    // In watch mode we auto-run (no input) so replays animate.
    if (mode !== 'play') {
      inputEnabledRef.current = false;
      setStatus('running');
      setEndedReason(null);
      setEndedAtMs(0);
      setScore(ghostRun?.score ?? 0);
      setPracticeActive(false);
    }
  }, [mode, ghostRun]);

  useEffect(() => {
    // If we transition from watch -> play, reset to a startable state.
    // Without this, the canvas can get stuck in "running" with input disabled,
    // making "practice" work while the real game never starts.
    if (mode === 'play') {
      inputEnabledRef.current = false;
      setPracticeActive(false);
      setStatus('ready');
      setEndedReason(null);
      setEndedAtMs(0);
      setScore(0);
    }
  }, [mode]);

  const effectiveMode = (mode === 'play' || practiceActive) ? 'play' : 'watch';
  const isTimed = Number.isFinite(config.maxMs);

  const flap = useCallback(() => {
    if (!inputEnabledRef.current) return;
    const s = stateRef.current;
    s.birdV = config.flapVel;
    // Jump particles
    s.particles.push(...Array.from({ length: 6 }).map((_, i) => ({
      id: `${Date.now()}-jump-${i}`,
      x: config.birdX - 18,
      y: s.birdY,
      vx: (Math.random() - 0.5) * 120,
      vy: (Math.random() - 0.5) * 120,
      size: 3 + Math.random() * 4,
      color: '#FF8C00',
      life: 1,
      decay: 0.9 + Math.random() * 0.6,
    })));
    const t = Math.max(0, Math.floor(s.timeMs));
    flapTimesRef.current.push(t);
    if (typeof onFlap === 'function') {
      try {
        onFlap({ tMs: t });
      } catch {
        // ignore callback failures
      }
    }
  }, [config.flapVel, onFlap]);

  const startRun = useCallback(({ force = false } = {}) => {
    if (disabled) return;
    if (mode !== 'play') return;
    if (!force && status !== 'ready') return;

    setStatus('running');
    inputEnabledRef.current = true;

    const s = stateRef.current;
    const generator = pipeGeneratorRef.current || createPipeGenerator(seed);
    pipeGeneratorRef.current = generator;
    generator.reset();
    const initialPipes = generator.generateInitialPipes({
      score: 0,
      height: config.height,
      width: config.width,
      gapMargin: config.gapMargin,
      groundHeight: 40,
      count: 10,
      spacing: config.basePipeSpacing,
    });

    s.timeMs = 0;
    s.birdY = 230;
    s.birdV = 0;
    s.pipes = initialPipes;
    s.pipeIndex = initialPipes.length;
    s.score = 0;
    s.coinsCollected = 0;

    // ghost
    s.ghostTimeMs = 0;
    s.ghostY = 230;
    s.ghostV = 0;
    s.ghostFlapIndex = 0;

    flapTimesRef.current = [];
    setScore(0);
    setCoinsCollected(0);
    setEndedReason(null);
    setEndedAtMs(0);
  }, [config, disabled, mode, seed, status]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const code = e.code;
      const key = e.key;
      const isFlapKey = code === 'Space' || code === 'ArrowUp' || key === ' ' || key === 'ArrowUp' || key === 'w' || key === 'W';
      if (isFlapKey) {
        e.preventDefault();
        if (mode === 'play' && effectiveMode === 'play' && status === 'ready' && !disabled) startRun({ force: true });
        flap();
      }
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disabled, effectiveMode, flap, mode, startRun, status]);

  const start = () => startRun({ force: false });

  const restart = () => startRun({ force: true });

  const startPractice = () => {
    if (!allowPractice) return;
    if (mode === 'play') return;
    if (status !== 'running' && status !== 'ready') return;

    setPracticeActive(true);
    setStatus('running');
    inputEnabledRef.current = true;

    const s = stateRef.current;
    const generator = pipeGeneratorRef.current || createPipeGenerator(seed);
    pipeGeneratorRef.current = generator;
    generator.reset();
    const initialPipes = generator.generateInitialPipes({
      score: 0,
      height: config.height,
      width: config.width,
      gapMargin: config.gapMargin,
      groundHeight: 40,
      count: 10,
      spacing: config.basePipeSpacing,
    });

    s.timeMs = 0;
    s.birdY = 230;
    s.birdV = 0;
    s.pipes = initialPipes;
    s.pipeIndex = initialPipes.length;
    s.score = 0;
    s.coinsCollected = 0;

    s.particles = [];
    s.coinEffects = [];

    // ghost stays as-is; practice is just for feel.
    flapTimesRef.current = [];

    setScore(0);
    setCoinsCollected(0);
    setEndedReason(null);
    setEndedAtMs(0);
  };

  const endRun = useCallback(({ reason, atMs }) => {
    if (status === 'ended') return;

    inputEnabledRef.current = false;
    setStatus('ended');
    setEndedReason(reason);
    setEndedAtMs(atMs);
    setScore(stateRef.current.score);

    if (mode === 'play' && typeof onFinish === 'function') {
      onFinish({
        score: stateRef.current.score,
        coinsCollected: stateRef.current.coinsCollected,
        endedReason: reason,
        endedAtMs: atMs,
        flapTimes: flapTimesRef.current.slice(0),
      });
    }
  }, [mode, onFinish, status]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const fixedDtMs = 1000 / 60;
    let lastTs = null;
    let accumulator = 0;

    const draw = () => {
      const { width, height } = config;
      const s = stateRef.current;
      const displayScore = effectiveMode === 'play' ? s.score : Number(ghostRun?.score ?? 0);
      const scoreLabel = effectiveMode === 'play' ? 'Score' : (ghostRun ? 'Ghost score' : 'Score');
      const isTimed = Number.isFinite(config.maxMs);
      const { scrollSpeed } = computeDifficultyParams({ score: s.score, timeMs: s.timeMs }, config);
      const targetGapSize = gapSizeFromProgress({ score: s.score, timeMs: s.timeMs }, config);

      // Background
      ctx.clearRect(0, 0, width, height);
      const assets = assetsRef.current;
      if (assets?.background) {
        ctx.drawImage(assets.background, 0, 0, width, height);
      } else {
        ctx.fillStyle = '#0b1320';
        ctx.fillRect(0, 0, width, height);
      }

      // Clouds
      s.clouds.forEach((cloud) => {
        ctx.beginPath();
        ctx.arc(cloud.x, cloud.y, cloud.size * 0.3, 0, Math.PI * 2);
        ctx.arc(cloud.x + cloud.size * 0.2, cloud.y - cloud.size * 0.1, cloud.size * 0.25, 0, Math.PI * 2);
        ctx.arc(cloud.x - cloud.size * 0.2, cloud.y - cloud.size * 0.1, cloud.size * 0.25, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.75)';
        ctx.fill();
      });

      // Ground
      ctx.fillStyle = '#101b2d';
      ctx.fillRect(0, height - 40, width, 40);

      // Pipes
      const pipeWidth = config.pipeWidth;
      // Use a pattern when available
      if (assetsRef.current?.pipeTexture) {
        if (!assetsRef.current._pipePattern) {
          assetsRef.current._pipePattern = ctx.createPattern(assetsRef.current.pipeTexture, 'repeat');
        }
        ctx.fillStyle = assetsRef.current._pipePattern || '#1faa59';
      } else {
        ctx.fillStyle = '#1faa59';
      }
      s.pipes.forEach((p) => {
        const pipeGap = Number.isFinite(p.gapSize) ? p.gapSize : targetGapSize;
        const topH = p.gapY - pipeGap / 2;
        const bottomY = p.gapY + pipeGap / 2;
        ctx.fillRect(p.x, 0, pipeWidth, topH);
        ctx.fillRect(p.x, bottomY, pipeWidth, height - bottomY - 40);

        // Coins
        if (Array.isArray(p.coins)) {
          p.coins.forEach((coin) => {
            if (coin.collected) return;
            const cx = p.x + (coin.dx ?? 35);
            const cy = coin.y;
            const r = coin.radius ?? 8;

            const sprites = assetsRef.current?.coinSprites;
            const visual = getCoinVisualByValue(coin.value);
            const type = visual.type;
            const sprite = sprites?.[type];
            if (sprite) {
              const size = Math.max(16, r * 2.6);
              ctx.drawImage(sprite, cx - size / 2, cy - size / 2, size, size);
            } else {
              // fallback circles
              ctx.beginPath();
              ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
              ctx.fillStyle = `${coin.color || visual.color}40`;
              ctx.fill();
              ctx.beginPath();
              ctx.arc(cx, cy, r, 0, Math.PI * 2);
              ctx.fillStyle = coin.color || visual.color;
              ctx.fill();
            }
          });
        }
      });

      // Ghost (if provided)
      if (ghostRun) {
        const ghostEndedAtMs = Math.max(0, Number(ghostRun.ended_at_ms ?? ghostRun.endedAtMs ?? 0));
        const showGhost = s.ghostTimeMs <= ghostEndedAtMs;

        if (showGhost) {
          ctx.globalAlpha = 0.6;
          ctx.fillStyle = '#4cc9f0';
          ctx.beginPath();
          ctx.arc(config.birdX, s.ghostY, config.birdRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }

      // Particles
      if (assetsRef.current?.particle) {
        for (const particle of s.particles) {
          ctx.globalAlpha = clamp(particle.life, 0, 1);
          ctx.drawImage(
            assetsRef.current.particle,
            particle.x - particle.size,
            particle.y - particle.size,
            particle.size * 2,
            particle.size * 2,
          );
        }
        ctx.globalAlpha = 1;
      } else {
        for (const particle of s.particles) {
          ctx.globalAlpha = clamp(particle.life, 0, 1);
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fillStyle = particle.color;
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      // Coin effects (floating +value)
      ctx.font = 'bold 16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const effect of s.coinEffects) {
        ctx.globalAlpha = clamp(effect.opacity, 0, 1);
        ctx.fillStyle = effect.color || '#FFD700';
        ctx.fillText(`+${effect.value}`, effect.x, effect.y);
      }
      ctx.globalAlpha = 1;

      // Bird (sprite frames if available)
      const birdFrames = assetsRef.current?.birdFrames;
      const birdSprite = birdFrames?.[s.birdFrame % (birdFrames?.length || 1)];
      if (birdSprite) {
        const size = 44;
        const tilt = clamp(s.birdV / 500, -0.6, 0.8);
        ctx.save();
        ctx.translate(config.birdX, s.birdY);
        ctx.rotate(tilt);
        ctx.drawImage(birdSprite, -size / 2, -size / 2, size, size);
        ctx.restore();
      } else {
        const birdColor = effectiveMode === 'play' ? '#ffb703' : 'rgba(233,236,239,0.55)';
        ctx.fillStyle = birdColor;
        ctx.beginPath();
        ctx.arc(config.birdX, s.birdY, config.birdRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.45)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      if (effectiveMode !== 'play' && !ghostRun) {
        ctx.fillStyle = 'rgba(233,236,239,0.65)';
        ctx.font = '13px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        ctx.fillText('Waiting for a run to replay… (or start Practice)', 16, config.height - 14);
      }

      // HUD: left score/coins and right time with a readable backdrop.
      const hudPadding = 10;
      const hudRadius = 10;
      const leftX = 16;
      const topY = 16;
      const lineHeight = 24;
      const hudFont = 'bold 20px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

      ctx.font = hudFont;
      const scoreText = `${scoreLabel} ${displayScore}`;
      const coinText = `Coins ${s.coinsCollected}`;
      const leftTextWidth = Math.max(ctx.measureText(scoreText).width, ctx.measureText(coinText).width);
      const leftBoxW = Math.ceil(leftTextWidth + hudPadding * 2);
      const leftBoxH = hudPadding * 2 + lineHeight * 2;

      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.strokeStyle = 'rgba(255,255,255,0.22)';
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, leftX, topY, leftBoxW, leftBoxH, hudRadius);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#f8f9fa';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(scoreText, leftX + hudPadding, topY + hudPadding);
      ctx.fillText(coinText, leftX + hudPadding, topY + hudPadding + lineHeight);

      if (isTimed) {
        const tLeft = Math.max(0, Math.ceil((config.maxMs - s.timeMs) / 1000));
        const timeText = `Time ${tLeft}s`;
        const timeTextWidth = ctx.measureText(timeText).width;
        const timeBoxW = Math.ceil(timeTextWidth + hudPadding * 2);
        const timeBoxH = hudPadding * 2 + lineHeight;
        const timeX = width - timeBoxW - 16;

        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.strokeStyle = 'rgba(255,255,255,0.28)';
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, timeX, topY, timeBoxW, timeBoxH, hudRadius);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#ffffff';
        ctx.fillText(timeText, timeX + hudPadding, topY + hudPadding);
      }
    };

    const step = (dtMs) => {
      const s = stateRef.current;
      if (status !== 'running') return;

      const isTimed = Number.isFinite(config.maxMs);

      // Watch mode: advance pipes + ghost only (no player physics/collisions)
      if (effectiveMode !== 'play') {
        const dt = dtMs / 1000;
        s.timeMs = isTimed ? Math.min(config.maxMs, s.timeMs + dtMs) : (s.timeMs + dtMs);
        s.pipes.forEach((p) => {
          p.x -= config.baseScrollSpeed * dt;
        });

        // Cleanup offscreen pipes
        s.pipes = s.pipes.filter((p) => (p.x + config.pipeWidth) > -120);

        // Spawn pipes ahead (fixed spacing/gap)
        const generator = pipeGeneratorRef.current || createPipeGenerator(seed);
        pipeGeneratorRef.current = generator;
        while (s.pipes.length < 140) {
          const lastX = s.pipes.length ? s.pipes[s.pipes.length - 1].x : -Infinity;
          if (lastX >= (config.width + 900)) break;

          const spacing = Math.max(config.minPipeSpacing, config.basePipeSpacing - Math.floor((s.timeMs / 1000) * 0.5));
          const x = (s.pipes.length === 0) ? (config.width + 60) : (lastX + spacing);
          const newPipe = generator.generateNextPipe({
            score: 0,
            x,
            height: config.height,
            gapMargin: config.gapMargin,
            groundHeight: 40,
          });
          newPipe.gapSize = gapSizeFromProgress({ score: s.score, timeMs: s.timeMs }, config);
          newPipe.coins = [];
          s.pipes.push(newPipe);
          s.pipeIndex = generator.pipeIndex;
        }

        if (ghostRun) {
          const ghostEndedAtMs = Math.max(0, Number(ghostRun.ended_at_ms ?? ghostRun.endedAtMs ?? 0));
          const ghostFlaps = Array.isArray(ghostRun.flap_times) ? ghostRun.flap_times
            : Array.isArray(ghostRun.flapTimes) ? ghostRun.flapTimes
              : [];

          const ghostActive = s.ghostTimeMs <= ghostEndedAtMs;
          if (ghostActive) {
            s.ghostTimeMs = Math.min(config.maxMs, s.ghostTimeMs + dtMs);
            while (s.ghostFlapIndex < ghostFlaps.length && s.ghostTimeMs >= Number(ghostFlaps[s.ghostFlapIndex])) {
              s.ghostV = config.flapVel;
              s.ghostFlapIndex += 1;
            }
            s.ghostV += config.gravity * dt;
            s.ghostY += s.ghostV * dt;
          }
        }

        if (isTimed && s.timeMs >= config.maxMs) {
          setEndedReason('time');
          setEndedAtMs(config.maxMs);
          setStatus('ended');
        }
        return;
      }

      s.timeMs = isTimed ? Math.min(config.maxMs, s.timeMs + dtMs) : (s.timeMs + dtMs);

      const { scrollSpeed } = computeDifficultyParams({ score: s.score, timeMs: s.timeMs }, config);
      const gapSize = gapSizeFromProgress({ score: s.score, timeMs: s.timeMs }, config);

      // Bird physics
      const dt = dtMs / 1000;
      s.birdV += config.gravity * dt;
      s.birdY += s.birdV * dt;

      // Pipe movement
      s.pipes.forEach((p) => {
        p.x -= scrollSpeed * dt;
      });

      // Animate clouds
      for (const cloud of s.clouds) {
        cloud.x -= cloud.speed * dt;
        if (cloud.x < -cloud.size) cloud.x = config.width + cloud.size;
      }

      // Animate bird wing frames
      s.birdFrameTimerMs += dtMs;
      if (s.birdFrameTimerMs >= 150) {
        s.birdFrameTimerMs = 0;
        s.birdFrame = (s.birdFrame + 1) % 3;
      }

      // Update coin effects
      s.coinEffects = s.coinEffects
        .map((e) => ({
          ...e,
          timeMs: e.timeMs + dtMs,
          y: e.y - 40 * dt,
          opacity: 1 - (e.timeMs / 900),
        }))
        .filter((e) => e.timeMs < 900);

      // Update particles
      s.particles = s.particles
        .map((p) => ({
          ...p,
          x: p.x + p.vx * dt,
          y: p.y + p.vy * dt,
          life: p.life - (p.decay * dt),
          size: p.size * 0.985,
        }))
        .filter((p) => p.life > 0);

      // Cleanup offscreen pipes
      s.pipes = s.pipes.filter((p) => (p.x + config.pipeWidth) > -120);

      // Spawn new pipes ahead (endless, consistent spacing)
      const generator = pipeGeneratorRef.current || createPipeGenerator(seed);
      pipeGeneratorRef.current = generator;
      while (s.pipes.length < 140) {
        const lastX = s.pipes.length ? s.pipes[s.pipes.length - 1].x : -Infinity;
        if (lastX >= (config.width + 900)) break;

        const spacing = Math.max(config.minPipeSpacing, config.basePipeSpacing - Math.floor((s.timeMs / 1000) * 0.5));
        const x = (s.pipes.length === 0) ? (config.width + 60) : (lastX + spacing);
        const newPipe = generator.generateNextPipe({
          score: s.score,
          x,
          height: config.height,
          gapMargin: config.gapMargin,
          groundHeight: 40,
        });
        newPipe.gapSize = gapSize;
        s.pipes.push(newPipe);
        s.pipeIndex = generator.pipeIndex;
      }

      // Scoring
      s.pipes.forEach((p) => {
        if (!p.passed && p.x + config.pipeWidth < config.birdX - config.birdRadius) {
          p.passed = true;
          s.score += 1;
          setScore(s.score);
        }
      });

      // Coin collection
      for (const p of s.pipes) {
        if (!Array.isArray(p.coins) || !p.coins.length) continue;
        for (const coin of p.coins) {
          if (coin.collected) continue;
          const cx = p.x + (coin.dx ?? 35);
          const cy = coin.y;
          const r = coin.radius ?? 8;
          const dx = config.birdX - cx;
          const dy = s.birdY - cy;
          if ((dx * dx + dy * dy) < ((config.birdRadius + r) * (config.birdRadius + r))) {
            coin.collected = true;
            s.coinsCollected += 1;
            const value = Math.max(0, Number(coin.value || 0));
            const visual = getCoinVisualByValue(value);
            s.score += value;
            setCoinsCollected(s.coinsCollected);
            setScore(s.score);

            // Effect + particles + sound
            s.coinEffects.push({
              id: `${coin.id || 'coin'}-effect-${Date.now()}`,
              x: config.birdX,
              y: s.birdY - 10,
              value,
              color: coin.color || visual.color,
              timeMs: 0,
              opacity: 1,
            });

            for (let i = 0; i < 16; i += 1) {
              s.particles.push({
                id: `${Date.now()}-spark-${i}`,
                x: cx,
                y: cy,
                vx: (Math.random() - 0.5) * 220,
                vy: (Math.random() - 0.5) * 220,
                size: 3 + Math.random() * 4,
                color: coin.color || visual.color,
                life: 1,
                decay: 0.8 + Math.random() * 0.8,
              });
            }

            playCoinSound(value);
          }
        }
      }

      // Ghost physics (replay)
      if (ghostRun) {
        const ghostEndedAtMs = Math.max(0, Number(ghostRun.ended_at_ms ?? ghostRun.endedAtMs ?? 0));
        const ghostFlaps = Array.isArray(ghostRun.flap_times) ? ghostRun.flap_times
          : Array.isArray(ghostRun.flapTimes) ? ghostRun.flapTimes
            : [];

        const ghostActive = s.ghostTimeMs <= ghostEndedAtMs;
        if (ghostActive) {
          s.ghostTimeMs = Math.min(config.maxMs, s.ghostTimeMs + dtMs);

          while (s.ghostFlapIndex < ghostFlaps.length && s.ghostTimeMs >= Number(ghostFlaps[s.ghostFlapIndex])) {
            s.ghostV = config.flapVel;
            s.ghostFlapIndex += 1;
          }

          s.ghostV += config.gravity * dt;
          s.ghostY += s.ghostV * dt;
        }
      }

      // Collisions
      const hitTop = s.birdY - config.birdRadius < 0;
      const hitBottom = s.birdY + config.birdRadius > config.height - 40;

      let hitPipe = false;
      for (const p of s.pipes) {
        const withinX = (config.birdX + config.birdRadius) > p.x && (config.birdX - config.birdRadius) < (p.x + config.pipeWidth);
        if (!withinX) continue;

        const pipeGap = Number.isFinite(p.gapSize) ? p.gapSize : gapSize;
        const gapTop = p.gapY - pipeGap / 2;
        const gapBottom = p.gapY + pipeGap / 2;
        const withinGap = (s.birdY - config.birdRadius) >= gapTop && (s.birdY + config.birdRadius) <= gapBottom;
        if (!withinGap) {
          hitPipe = true;
          break;
        }
      }

      if (hitTop || hitBottom || hitPipe) {
        // Crash particles
        for (let i = 0; i < 22; i += 1) {
          s.particles.push({
            id: `${Date.now()}-crash-${i}`,
            x: config.birdX,
            y: s.birdY,
            vx: (Math.random() - 0.5) * 260,
            vy: (Math.random() - 0.5) * 260,
            size: 3 + Math.random() * 6,
            color: '#ff4d4d',
            life: 1,
            decay: 0.7 + Math.random() * 0.9,
          });
        }
        endRun({ reason: 'crash', atMs: Math.floor(s.timeMs) });
        return;
      }

      if (isTimed && s.timeMs >= config.maxMs) {
        endRun({ reason: 'time', atMs: config.maxMs });
      }
    };

    const loop = (ts) => {
      if (lastTs == null) lastTs = ts;
      const delta = ts - lastTs;
      lastTs = ts;
      accumulator += delta;

      while (accumulator >= fixedDtMs) {
        step(fixedDtMs);
        accumulator -= fixedDtMs;
      }

      draw();
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [config, ghostRun, status, mode, endedReason, practiceActive, effectiveMode, endRun, seed]);

  return (
    <div>
      <div className="flappy-canvas-wrap">
        <canvas
          ref={canvasRef}
          width={config.width}
          height={config.height}
          className="flappy-canvas"
          onPointerDown={(e) => {
            if (effectiveMode !== 'play') return;

            // Ignore secondary touches (multi-touch) which can cause double flaps.
            if (e.isPrimary === false) return;

            // Small cooldown to avoid overly-sensitive rapid taps on mobile.
            const now = Date.now();
            if (now - lastPointerFlapAtRef.current < 60) return;
            lastPointerFlapAtRef.current = now;

            e.preventDefault();
            if (mode === 'play' && status === 'ready' && !disabled) {
              startRun({ force: true });
            }
            flap();
          }}
        />

        <div className="flappy-rotate-overlay">
          <div className="flappy-rotate-card">
            <div className="flappy-rotate-title">Rotate to landscape</div>
            <div className="flappy-rotate-sub">Flappy is optimized for wide screens.</div>
          </div>
        </div>

        {mode === 'play' && effectiveMode === 'play' && (status === 'ready' || status === 'ended') && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 18,
              background: 'rgba(0,0,0,0.40)',
              borderRadius: 12,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <button
                className="flappy-btn-primary"
                onClick={() => {
                  if (status === 'ended') restart();
                  else start();
                }}
                disabled={disabled}
                style={{ minWidth: 180 }}
              >
                {status === 'ended' ? 'Restart' : 'Start'}
              </button>
              <div className="flappy-canvas-instructions" style={{ marginTop: 10 }}>
                Controls: Space / ↑ / W / Click / Tap
              </div>
              <div className="flappy-canvas-subtext" style={{ marginTop: 4 }}>
                Difficulty ramps up as your score increases.
              </div>
            </div>
          </div>
        )}
      </div>

      {mode === 'play' && (
        <div className="flappy-canvas-controls" style={{ marginTop: 10 }}>
          <button
            className="flappy-btn"
            onClick={start}
            disabled={disabled || status !== 'ready'}
          >
            Start Run
          </button>
          <div className="flappy-canvas-status">
            {status === 'ready' && (
              isTimed
                ? `Your run is ${Math.round(config.maxMs / 1000)} seconds.`
                : 'Endless run — survive as long as you can (ends on crash).'
            )}
            {status === 'running' && 'Running...'}
            {status === 'ended' && `Ended (${endedReason})`}
          </div>
        </div>
      )}

      {mode !== 'play' && allowPractice && (
        <div className="flappy-canvas-controls" style={{ marginTop: 10, flexWrap: 'wrap' }}>
          <button
            className="flappy-btn"
            onClick={startPractice}
            disabled={false}
          >
            Practice Run (not submitted)
          </button>
          <div className="flappy-canvas-status">
            Watching match. Practice won’t affect the duel.
          </div>
        </div>
      )}

      <div className="flappy-canvas-hud" style={{ marginTop: 6 }}>
        {effectiveMode === 'play' ? 'Controls: Space / Click / Tap' : 'Viewing mode'}
      </div>

      <div className="flappy-canvas-hud" style={{ marginTop: 6 }}>
        Score: {score} • Coins: {coinsCollected} {endedReason ? `• Ended: ${endedReason} @ ${endedAtMs}ms` : ''}
      </div>
    </div>
  );
}
